'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

interface UseIntersectionAnimationOptions {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
}

export function useIntersectionAnimation(options: UseIntersectionAnimationOptions = {}) {
  const { threshold = 0.1, rootMargin = '0px', triggerOnce = true } = options;
  const [isVisible, setIsVisible] = useState(false);
  const [hasEnteredView, setHasEnteredView] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const observedElementRef = useRef<Element | null>(null);

  const disconnect = useCallback(() => {
    if (observerRef.current && observedElementRef.current) {
      try {
        observerRef.current.unobserve(observedElementRef.current);
      } catch (_) {}
      observerRef.current = null;
      observedElementRef.current = null;
    }
  }, []);

  const refCallback = useCallback(
    (el: HTMLDivElement | HTMLButtonElement | null) => {
      if (el === observedElementRef.current) return;
      disconnect();
      if (!el) return;

      observedElementRef.current = el;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            setHasEnteredView(true);
            if (triggerOnce) {
              disconnect();
            }
          } else if (!triggerOnce) {
            setIsVisible(false);
          }
        },
        { threshold, rootMargin }
      );
      observer.observe(el);
      observerRef.current = observer;
    },
    [threshold, rootMargin, triggerOnce, disconnect]
  );

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return { ref: refCallback, isVisible, hasEnteredView };
}
